import { EmptyState } from '../components/EmptyState.js';
import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { apiGet } from '../lib/api.js';
import type { ComparisonData, ComparisonResponse, PromptTemplateListResponse } from '../types/index.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Select } from '../components/Select.js';
import { Region } from '../components/ui/Region.js';
import { Chip } from '../components/ui/Chip.js';
import type { ChipProps } from '../components/ui/Chip.js';

const ENGINE_LABELS: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  google_aio: 'Google AIO',
};

// Spec 235 task 3 — the chip-tone mapping (evidence.md): green -> ok,
// red -> fail. Neutral is a bare Chip (no tone).
const SENTIMENT_TONE: Record<string, ChipProps['tone']> = {
  positive: 'ok',
  neutral: undefined,
  negative: 'fail',
};

function SentimentBadge({ label }: { label: string | null }) {
  if (!label) return null;
  return <Chip tone={SENTIMENT_TONE[label]}>{label}</Chip>;
}

export function ComparisonPage() {
  const { selectedBrand, user } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState<{ id: string; template: string }[]>([]);
  const [responses, setResponses] = useState<ComparisonResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTemplateId = searchParams.get('prompt_template_id') ?? '';

  // Fetch prompt templates
  useEffect(() => {
    if (!selectedBrand || !user) return;
    let cancelled = false;
    async function load() {
      try {
        const url = `/api/v1/tenants/${selectedBrand!.tenant_id}/brands/${selectedBrand!.id}/prompt-templates?limit=100`;
        const result = await apiGet<PromptTemplateListResponse>(url);
        if (!cancelled) {
          setTemplates(result.data.map((t) => ({ id: t.id, template: t.template })));
        }
      } catch {
        // ignore
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedBrand, user]);

  // Fetch comparison data
  const fetchComparison = useCallback(async () => {
    if (!selectedBrand || !user || !selectedTemplateId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/responses/compare?prompt_template_id=${selectedTemplateId}`;
      const result = await apiGet<ComparisonData>(url);
      setResponses(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, selectedTemplateId]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  if (!selectedBrand) {
    return (
      <div className="hub-page--table">
        <h1 className="hub-page__title">Cross-engine comparison</h1>
        <EmptyState icon="bx-columns" title="Select a brand" description="" />
      </div>
    );
  }

  return (
    <div data-testid="comparison-page" className="hub-page--table">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Cross-engine comparison</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">Compare AI responses across engines for {selectedBrand.name}</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Select prompt template</label>
        <Select
          options={[
            { value: '', label: 'Choose a prompt...' },
            ...templates.map((t) => ({
              value: t.id,
              label: t.template.length > 80 ? t.template.substring(0, 80) + '...' : t.template,
            })),
          ]}
          value={selectedTemplateId}
          onChange={(v) => setSearchParams({ prompt_template_id: v })}
          placeholder="Choose a prompt..."
          className="mt-1 max-w-lg"
          data-testid="template-selector"
        />
      </div>

      {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="mt-8 flex justify-center"><Spinner className="text-violet-600" /></div>}

      {!loading && !selectedTemplateId && (
        <Region className="mt-6" data-testid="comparison-placeholder">
          <div className="hub-stats" style={{ '--hub-stats-count': 4 } as CSSProperties}>
            {['ChatGPT', 'Gemini', 'Claude', 'Google AIO'].map((label) => (
              <div key={label} className="hub-stat opacity-60">
                <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
                <p className="mt-2 text-center text-xs text-gray-400">Select a prompt to compare</p>
              </div>
            ))}
          </div>
        </Region>
      )}

      {!loading && selectedTemplateId && responses.length === 0 && (
        <div className="mt-8"><EmptyState icon="bx-columns" title="No responses found for this prompt" description="" testId="no-comparison" /></div>
      )}

      {!loading && responses.length > 0 && (
        <Region className="mt-6" data-testid="comparison-grid">
          {/* Spec 235 §3.2 — one column per engine IS the feature (product spec
              §4.7's side-by-side view), so this stays a grid, never rows. Only
              the card chrome moves: each column's own rounded-lg/border/bg-white
              becomes a cell that touches its neighbours, divided by a VERTICAL
              hairline — the same treatment .hub-stats already gives N cells
              that touch, now sitting on one shared Region surface instead of
              N separate white cards floating on the page ground. */}
          <div className="hub-stats" style={{ '--hub-stats-count': responses.length } as CSSProperties}>
            {responses.map((r) => (
              <div key={r.id} className="hub-stat" data-testid={`engine-column-${r.engine}`}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">{ENGINE_LABELS[r.engine] ?? r.engine}</h3>
                  <SentimentBadge label={r.sentiment_label} />
                </div>
                {r.api_model && <p className="mb-2 text-xs text-gray-400">Model: {r.api_model}</p>}
                <div className="mb-2 flex gap-2 text-xs text-gray-500">
                  {r.brand_mentioned !== null && (
                    <span className={r.brand_mentioned ? 'text-green-600' : 'text-red-600'}>
                      {r.brand_mentioned ? 'Mentioned' : 'Not mentioned'}
                    </span>
                  )}
                  {r.latency_ms !== null && <span>{r.latency_ms}ms</span>}
                </div>
                <div className="max-h-64 overflow-y-auto rounded-md bg-gray-50 p-2 text-xs text-gray-700 whitespace-pre-wrap">
                  {r.raw_content}
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  {new Date(r.captured_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </Region>
      )}
    </div>
  );
}
